/* eslint-disable consistent-return */
import { MutableRefObject, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DeviceInfo, ls } from '@ezugi/utils';

import { MEDIA } from '../../../../constants';
import { mediaActions, videoActions } from '../../../../store/actions';
import {
  volumeSelector,
  isDisconnectedSelector,
} from '../../../../store/selectors';
import { retailModeSelector } from '../../../../store/selectors/app';
import { logError } from '../../../../util/logUtils';
import { updateVideoVolume } from '../../../../util/mediaUtils';
import { isSoundAllowed } from '../isSoundAllowed';
import { EvoPlayerInstance } from '../player';

const { volume, autoplaymute } = mediaActions;

export const wallclockRef: MutableRefObject<number | null> = { current: null };

export const usePlayerEvents = (player: EvoPlayerInstance) => {
  const volumeState = useSelector(volumeSelector);
  const disconnected = useSelector(isDisconnectedSelector);
  const retailMode = useSelector(retailModeSelector);

  const dispatch = useDispatch();

  const updateWallclock = ({ wallclock }: { wallclock: number }) => {
    wallclockRef.current = wallclock;
  };

  const setAvailableQualities = useCallback(
    (availableQualities: string[]) => dispatch(
      videoActions.video.set({
        availableQualities: [ ...availableQualities ].reverse(),
      })
    ),
    [ dispatch ]
  );

  const setUserInteractionRequired = useCallback(async () => {
    // return early if setUserInteractionRequired is re-triggered by player re-mounting
    // if canPlaySound is true, we already have user interaction
    // this case occurs when using flipbook
    if (player.canPlaySound) {
      return;
    }

    // on mobile we always mute and wait for tap to play to unmute
    if (DeviceInfo.isHandheld) {
      // we can only control safari via physical buttons
      updateVideoVolume({ ...volumeState, muted: true });
      dispatch(autoplaymute.set(false));
      return;
    }

    // autoplay policy guard
    // overwrite isUserInteractionRequired from Evo using isSoundAllowed

    // on Safari isSoundAllowed can return false positives
    // so Safari always requires user interaction.
    const canPlaySound = DeviceInfo.isSafari ? false : await isSoundAllowed();
    player.canPlaySound = canPlaySound;

    // On retail games we need to set the volume to default and unmuted
    // we don't care about localStorage
    if (retailMode) {
      dispatch(
        volume.set({
          ...volumeState,
          value: 50,
          muted: false,
        })
      );
      return;
    }

    const [ err, localStorageVolume ] = ls.get(MEDIA.VOLUME);

    if (err) {
      logError('Failed to get volume from localStorage', 'error');
    }

    dispatch(
      volume.set({
        ...volumeState,
        muted: err ? false : localStorageVolume.muted,
      })
    );
  }, [ player, dispatch, retailMode, volumeState ]);

  useEffect(() => {
    if (!player.initialized) return;

    /** Set available qualities required for the EvoPlayerQualitySelect component */
    player.availableQualities.subscribe(setAvailableQualities);

    player.wallclock.subscribe(updateWallclock);

    /**
     * Subscribe to isUserInteractionRequired to mute the player which
     * will automatically start playing
     */
    player.isUserInteractionRequired.subscribe(setUserInteractionRequired);

    return () => {
      player.availableQualities.unsubscribe(setAvailableQualities);
      player.wallclock.unsubscribe(updateWallclock);
      player.isUserInteractionRequired.unsubscribe(setUserInteractionRequired);

      wallclockRef.current = null;
    };
  }, [ player, disconnected, setAvailableQualities, setUserInteractionRequired ]);
};
